import clsx from 'clsx'

/* blocks */
import CustomerLogo from '@/cmp/organism/CustomerLogo/CustomerLogo'
import FooterCta from '@/cmp/organism/FooterCta/FooterCta'
import PageHero from '@/cmp/organism/PageHero/PageHero'
import CreatorMoreInfo from '@/templates/CreatorPlatform/CreatorMoreInfo/CreatorMoreInfo'
import CreatorTestimonial from './components/CreatorTestimonial'
import CreatorRecognition from './components/CreatorRecognition'
import FeatureDropdown from '@/cmp/organism/FeatureDropdown/FeatureDropdown'

/* utils */
import { ProductCreatorAttributes } from '@/utils/queries/platform_creator'

/* styles */
import styles from './_.module.css'

interface Props {
  data: ProductCreatorAttributes
}

function Creator({ data }: Props): JSX.Element {
  return (
    <>
      <PageHero
        class={clsx(styles.description)}
        layout="left"
        primaryHeadline={data.hero.title}
        subText={data.hero.description}
        image={data.hero.image?.data?.attributes}
        primaryCta={data.hero.primaryCta}
        secondaryCta={data.hero.secondaryCta}
        footNote={data.hero.footnote}
      />

      {data.brandList.brands.data.length > 0 ? (
        <CustomerLogo
          title={data.brandList.title}
          brands={data.brandList.brands.data.map(i => ({
            ...i.attributes.logo.data.attributes,
            alternativeText: i.attributes.name
          }))}
        />
      ) : null}

      {data.featureListDropdownFirst.length > 0 ? (
        <FeatureDropdown
          title={data.featureTitleDropdownFirst.title ?? ''}
          description={data.featureTitleDropdownFirst.description ?? ''}
          list={data.featureListDropdownFirst.map(i => {
            return {
              id: i.id,
              title: i.title ?? '',
              description: i.description ?? '',
              media: i.multimedia?.media?.data?.attributes,
              bgColor: i.color ?? ''
            }
          })}
          iconTagDetails={{
            icon: data.featureTitleDropdownFirst.multimedia?.media?.data
              ?.attributes,
            tag: data.featureTitleDropdownFirst.additionalFields?.tag as string,
            color: data.featureTitleDropdownFirst.color ?? ''
          }}
        />
      ) : null}

      {data.featureListDropdownSecond.length > 0 ? (
        <FeatureDropdown
          title={data.featureTitleDropdownSecond.title ?? ''}
          description={data.featureTitleDropdownSecond.description ?? ''}
          list={data.featureListDropdownSecond.map(i => {
            return {
              id: i.id,
              title: i.title ?? '',
              description: i.description ?? '',
              media: i.multimedia?.media?.data?.attributes,
              bgColor: i.color ?? ''
            }
          })}
          iconTagDetails={{
            icon: data.featureTitleDropdownSecond.multimedia?.media?.data
              ?.attributes,
            tag: data.featureTitleDropdownSecond.additionalFields
              ?.tag as string,
            color: data.featureTitleDropdownSecond.color ?? ''
          }}
          layout="content-right"
        />
      ) : null}

      {data.recognitionList.length > 0 ? (
        <CreatorRecognition
          title={data.recognitionTitle}
          list={data.recognitionList.map(i => ({
            id: i.id,
            title: i.title ?? '',
            description: i.description ?? '',
            image: i.multimedia?.media?.data?.attributes
          }))}
        />
      ) : null}

      {data.testimonials.length > 0 ? (
        <CreatorTestimonial
          title={data.testimonialTitle}
          testimonials={data.testimonials.map(testimonial => {
            return {
              authorName: testimonial.authorName,
              authorDesignation: testimonial.authorDesignation,
              authorImage: testimonial.authorImage?.data?.attributes,
              review: testimonial.review
            }
          })}
        />
      ) : null}

      {data.moreInfoCard ? (
        <CreatorMoreInfo title={data.moreInfoTitle} list={data.moreInfoCard} />
      ) : null}

      {data.footerCTA.title ? (
        <FooterCta
          headline={data.footerCTA.title}
          variant="purple"
          mode="full_width"
          primaryCta={data.footerCTA.primaryCta}
          secondaryCta={data.footerCTA.secondaryCta}
          list={data.footerCTA.list?.map(i => ({
            id: i.id,
            item: i.Item
          }))}
        />
      ) : null}
    </>
  )
}

export default Creator
